import React, { useCallback } from "react";

import { css, cx } from "@emotion/css";
import { useHover } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { InclusionAgentDocSuggestion } from "@/models/InclusionAgentModel.ts";
import { patientFormattedName } from "@/models/UserModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { InclusionAgentSource } from "@/uiKit/molecules/InclusionCenter/InclusionAgent/InclusionAgentSource.tsx";
import { PatientSuggestionCard } from "@/uiKit/molecules/InclusionCenter/PatientSuggestionCard.tsx";

import { INCLUSION_AGENT_PHONE_FORM_ID, InclusionAgentPhoneForm } from "./InclusionAgentPhoneForm";
import { hasStats, InclusionAgentStats } from "./InclusionAgentStats";

export type InclusionAgentSuggestionProps = {
  suggestion: InclusionAgentDocSuggestion;
  onAction: () => void;
  className?: string;
};

export const InclusionAgentSuggestion: React.FC<InclusionAgentSuggestionProps> = ({
  suggestion,
  onAction,
  className,
}) => {
  const styles = useStyles(makeStyles);
  const [ref, hovered] = useHover<HTMLDivElement>();
  const { t } = useTranslation();
  const [acceptInfoOpened, setAcceptInfoOpened] = React.useState(false);
  const onClose = useCallback(() => setAcceptInfoOpened(false), []);
  const reject = Queries.practitioner.useRejectInclusionAgentSuggestion();

  return (
    <>
      <PatientSuggestionCard
        ref={ref}
        patientId={suggestion.id}
        patientFirstName={suggestion.firstName}
        patientLastName={suggestion.lastName}
        className={className}
        subHeaderTag={<Tag color="purple" label={t("nurseInclusion.inclusionProposal")} />}
        source={<InclusionAgentSource id={`inclusion-agent-suggestion-source-${suggestion.id}`} />}
        onDecline={() => {}}
        onValidate={() => {}}
        actions={
          <>
            <Button
              id="inclusion-agent-reject-suggestion-button"
              data-action="inclusion.agent-suggestion.reject"
              variant="contained"
              onClick={() =>
                reject.mutate(suggestion.id, {
                  onSuccess: () => {
                    TextToast.warning(t("inclusionAgent.rejected"), t("inclusionAgent.disappear"));
                    onAction();
                  },
                  onError: () => {
                    TextToast.error(t("errors.unknownError"));
                  },
                })
              }
            >
              {t("inclusionAgent.reject")}
            </Button>
            <Button
              key="display-form"
              data-action="inclusion.agent-suggestion.accept"
              id="inclusion-agent-accept-suggestion-button"
              variant="contained"
              onClick={() => setAcceptInfoOpened(true)}
            >
              {t("inclusionAgent.accept")}
            </Button>
          </>
        }
      >
        {(hasStats(suggestion) || !!suggestion.libreviewId) && (
          <div className={styles.statsOrLibreview}>
            <div
              className={cx({
                [styles.stats]: true,
                [styles.hidden]: acceptInfoOpened || hovered || !hasStats(suggestion),
              })}
            >
              <InclusionAgentStats suggestion={suggestion} />
            </div>
            <div
              className={cx({
                [styles.libreView]: true,
                [styles.hidden]: !acceptInfoOpened && !hovered && hasStats(suggestion),
              })}
            >
              <Button
                id="inclusion-agent-view-on-libreview-button"
                data-action="inclusion.agent-suggestion.open-libreview"
                variant="outlined"
                component="a"
                className={styles.libreViewButton}
                target="_blank"
                href={`https://www.libreview.com/patient/${suggestion.libreviewId}/reports`}
              >
                {t("inclusionAgent.viewOnLibreView")}
              </Button>
            </div>
          </div>
        )}
      </PatientSuggestionCard>
      <Dialog id={`inclusion-agent-phone-dialog-${suggestion.id}`} open={acceptInfoOpened} fitContent>
        <CardHeader title={patientFormattedName(suggestion.lastName, suggestion.firstName)} variant="contained" />
        <CardContent>
          <InclusionAgentPhoneForm suggestion={suggestion} onAction={onAction} onClose={onClose} />
        </CardContent>
        <CardFooter>
          <Button
            id="inclusion-agent-cancel-suggestion-with-phone-button"
            data-action="inclusion.agent-suggestion.cancel-with-phone"
            variant="outlined"
            onClick={() => setAcceptInfoOpened(false)}
          >
            {t("inclusionAgent.cancel")}
          </Button>
          <Button
            key="send-form"
            id="inclusion-agent-accept-suggestion-with-phone-button"
            data-action="inclusion.agent-suggestion.accept-with-phone"
            variant="contained"
            type="submit"
            form={INCLUSION_AGENT_PHONE_FORM_ID(suggestion)}
          >
            {t("inclusionAgent.accept")}
          </Button>
        </CardFooter>
      </Dialog>
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  content: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(8),
    padding: theme.spacing(5, 12),
  }),
  header: css({
    padding: theme.spacing(5, 12, 1, 12),
  }),
  statsOrLibreview: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    maxWidth: "100%",
    minWidth: 0,
    alignItems: "center",
    overflowX: "clip",
  }),
  stats: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    minHeight: theme.spacing(20),
    gap: theme.spacing(2),
    width: "100%",
    textWrap: "nowrap",
  }),
  libreView: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    width: "100%",
    textWrap: "nowrap",
  }),
  hidden: css({
    maxWidth: 0,
    minWidth: 0,
    width: 0,
    overflow: "clip",
  }),
  statKey: css({
    fontWeight: "bold",
  }),
  libreViewButton: css({
    backgroundColor: theme.palette.common.white,
  }),
  footer: css({
    display: "flex",
    gap: 0,
    padding: theme.spacing(5, 12),
    justifyContent: "flex-start",
  }),
  name: css({
    textWrap: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontWeight: "bold",
  }),
});
